import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import {
  LoadingOverlay,
  Group,
  Menu,
  ActionIcon,
  NumberInput,
  Stack,
  Text,
  Button,
  Tooltip,
} from "@mantine/core"
import { DepartmentDutyRosterSpreadsheet } from "../../-components/DutyScheduling/duty-roster-spreadsheet"
import { SuspenseWithErrorBoundary } from "../../../../-components/suspense"
import { useValidatedState } from "@mantine/hooks"
import { useTranslation } from "react-i18next"
import { Page } from "../../../../-components/Page"
import { keepPreviousData } from "@tanstack/react-query"
import { DatePeriodSelect } from "../orchestra/-components/period-select"
import { useGridStoreInitializer } from "../../-components/DutyScheduling/state"
import { DutySchedulingContainer } from "../-components/-DutySchedulingContainer"
import { useDepartmentDutyRosterGet, useGetFormulaResults } from "@/orval/gen"
import { dateToDateOnly } from "../../../../utils/date-to-date-only"
import { collectContractIds } from "../-components/collect-contracts"
import { DateOnly } from "../../../../@client/System"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"
import {
  IconLayoutSidebarRightCollapse,
  IconLayoutSidebarRightExpand,
  IconSettings,
} from "@tabler/icons-react"
import { DepartmentWorkareaTreeSelect } from "@components/tree-select/DepartmentWorkareaTreeSelect"
import { QuickAddShiftPanel } from "./-components/QuickAddShiftPanel"
import { QuickQueueStats } from "../../-components/DutyScheduling/duty-roster-spreadsheet"
import { useQuickAddPanelState } from "../-components/useQuickAddPanelState"
import { QuickQueueWidget } from "../-components/QuickQueueWidget"
import { useSelectedDepartmentIds } from "../-components/useSelectedDepartmentIds"

export const Route = createFileRoute("/app/duties/shifts-and-casts/")({
  component: RouteComponent,
  beforeLoad: ({ context: { t } }) => ({
    getTitle: () => t("DutyScheduling_ShiftsAndCasts"),
  }),
})

const d = new Date()
d.setDate(new Date().getDate() + 14)
const initialRange: [DateOnly | null, DateOnly | null] = [
  dateToDateOnly(new Date()),
  dateToDateOnly(d),
]

const QUICK_ADD_PANEL_VISIBLE_KEY =
  "duty-scheduling/shifts-and-casts/quick-add-panel-visible"
const QUICK_ADD_SELECTED_SHIFT_KEY =
  "duty-scheduling/shifts-and-casts/selected-quick-add-shift"

function normalizePositiveInt(value: string | number) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null
  }

  const rounded = Math.floor(value)
  return rounded > 0 ? rounded : null
}

function DutySchedulingSettingsMenu({
  fixedColumnsPerDay,
  setFixedColumnsPerDay,
  maxColumnsPerDay,
  setMaxColumnsPerDay,
}: {
  fixedColumnsPerDay: number | null
  setFixedColumnsPerDay: (value: number | null) => void
  maxColumnsPerDay: number | null
  setMaxColumnsPerDay: (value: number | null) => void
}) {
  const { t } = useTranslation("app_server")

  return (
    <Menu closeOnItemClick={false}>
      <Menu.Target>
        <ActionIcon variant="default" size={30} aria-label={t("Settings")}>
          <IconSettings />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Stack gap="xs" style={{ minWidth: 250 }}>
          <Text fw={600} size="sm">
            {t("Settings")}
          </Text>
          <NumberInput
            label={t("DutyScheduling_FixedColumnsPerDay")}
            description={t("DutyScheduling_FixedColumnsPerDay_Desc")}
            min={1}
            allowDecimal={false}
            value={fixedColumnsPerDay ?? ""}
            onChange={(v) => setFixedColumnsPerDay(normalizePositiveInt(v))}
          />
          <NumberInput
            label={t("DutyScheduling_MaxColumnsPerDay")}
            description={t("DutyScheduling_MaxColumnsPerDay_Desc")}
            min={1}
            allowDecimal={false}
            value={maxColumnsPerDay ?? ""}
            onChange={(v) => setMaxColumnsPerDay(normalizePositiveInt(v))}
          />
          <Group justify="flex-end">
            <Button
              variant="subtle"
              size="xs"
              onClick={() => {
                setFixedColumnsPerDay(null)
                setMaxColumnsPerDay(null)
              }}
            >
              {t("DutyScheduling_ResetColumns")}
            </Button>
          </Group>
        </Stack>
      </Menu.Dropdown>
    </Menu>
  )
}

function RouteComponent() {
  const { t } = useTranslation("app_server")
  const [{ value: range, lastValidValue: lastValidRange }, setRange] =
    useValidatedState(initialRange, (v) => Boolean(v[0] && v[1]))

  const [fixedColumnsPerDay, setFixedColumnsPerDay] = useUserLocalStorage<
    number | null
  >({
    key: "duty-scheduling/shifts-and-casts/fixed-columns-per-day",
    defaultValue: null,
  })
  const [maxColumnsPerDay, setMaxColumnsPerDay] = useUserLocalStorage<
    number | null
  >({
    key: "duty-scheduling/shifts-and-casts/max-columns-per-day",
    defaultValue: null,
  })
  const [
    storedSelectedWorkareasOrDepartments,
    setStoredSelectedWorkareasOrDepartments,
  ] = useUserLocalStorage<string[]>({
    key: "duty-scheduling/shifts-and-casts/selected-workareas-or-departments",
    defaultValue: [],
  })
  const [selectedWorkareasOrDepartments, setSelectedWorkareasOrDepartments] =
    useState<string[]>(storedSelectedWorkareasOrDepartments)
  const {
    quickAddPanelVisible,
    setQuickAddPanelVisible,
    selectedQuickAddShift,
    setSelectedQuickAddShift,
  } = useQuickAddPanelState({
    panelVisibleStorageKey: QUICK_ADD_PANEL_VISIBLE_KEY,
    selectedShiftStorageKey: QUICK_ADD_SELECTED_SHIFT_KEY,
  })

  useEffect(() => {
    setStoredSelectedWorkareasOrDepartments(selectedWorkareasOrDepartments)
  }, [selectedWorkareasOrDepartments, setStoredSelectedWorkareasOrDepartments])

  return (
    <Page>
      <Group>
        <DepartmentWorkareaTreeSelect
          w={200}
          size="xs"
          maw={"100%"}
          label=""
          placeholder={t("Departments")}
          value={selectedWorkareasOrDepartments}
          onChange={setSelectedWorkareasOrDepartments}
          params={{ withRoot: false, withModule: false }}
        />
        <DatePeriodSelect value={range} onChange={(v) => setRange(v)} />
        <DutySchedulingSettingsMenu
          fixedColumnsPerDay={fixedColumnsPerDay}
          setFixedColumnsPerDay={setFixedColumnsPerDay}
          maxColumnsPerDay={maxColumnsPerDay}
          setMaxColumnsPerDay={setMaxColumnsPerDay}
        />
        <Tooltip label={t("DutyScheduling_QuickAddMenu")}>
          <ActionIcon
            variant="default"
            size={30}
            aria-label={t("DutyScheduling_ToggleQuickAdd")}
            onClick={() => setQuickAddPanelVisible((v) => !v)}
          >
            {quickAddPanelVisible ? (
              <IconLayoutSidebarRightCollapse size={16} />
            ) : (
              <IconLayoutSidebarRightExpand size={16} />
            )}
          </ActionIcon>
        </Tooltip>
      </Group>
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: 8,
          height: "calc(100vh - 180px)",
          minHeight: 480,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <DutySchedulingContainer h="100%" mah="100%">
            {lastValidRange && lastValidRange[0] && lastValidRange[1] ? (
              <InnerComponent
                from={lastValidRange[0]}
                until={lastValidRange[1]}
                departmentTreeNodes={selectedWorkareasOrDepartments}
                fixedColumnsPerDay={fixedColumnsPerDay}
                maxColumnsPerDay={maxColumnsPerDay}
                showWorkingTime={true}
              />
            ) : null}
          </DutySchedulingContainer>
        </div>

        {quickAddPanelVisible ? (
          <div
            style={{
              flex: "0 0 280px",
              width: 280,
              minWidth: 280,
              height: "100%",
              minHeight: 0,
              marginTop: 10,
            }}
          >
            <QuickAddShiftPanel
              departmentTreeNodes={selectedWorkareasOrDepartments}
              selectedQuickAddShift={selectedQuickAddShift}
              setSelectedQuickAddShift={setSelectedQuickAddShift}
            />
          </div>
        ) : null}
      </div>
    </Page>
  )
}

function InnerComponent({
  from,
  until,
  departmentTreeNodes,
  fixedColumnsPerDay,
  maxColumnsPerDay,
  showWorkingTime,
}: {
  from: string
  until: string
  departmentTreeNodes: string[]
  fixedColumnsPerDay: number | null
  maxColumnsPerDay: number | null
  showWorkingTime: boolean
}) {
  const requestParams = {
    from,
    until,
    departmentTreeNodes,
    showWorkingTime,
    fixedColumnsPerDay: fixedColumnsPerDay ?? undefined,
    maxColumnsPerDay: maxColumnsPerDay ?? undefined,
  }

  const { data, refetch, isFetching, dataUpdatedAt } =
    useDepartmentDutyRosterGet(requestParams, {
      query: { placeholderData: keepPreviousData },
    })
  const [isQuickQueueLoading, setIsQuickQueueLoading] = useState(false)
  const [quickQueueStats, setQuickQueueStats] = useState<QuickQueueStats>({
    visible: false,
    pending: 0,
    total: 0,
    completed: 0,
    percent: 0,
    running: false,
    cancelRequested: false,
  })
  const [quickQueueCancel, setQuickQueueCancel] = useState<(() => void) | null>(
    null
  )
  const contractIds = collectContractIds(data?.rowHeaders || [])
  const departmentId = useSelectedDepartmentIds(departmentTreeNodes)[0]
  const { data: formulaResults } = useGetFormulaResults(
    {
      from,
      until,
      contractIds,
      departmentId,
      formulaIds: [],
    },
    {
      query: {
        placeholderData: keepPreviousData,
        enabled: Boolean(departmentId),
      },
    }
  )
  const formulaValues = formulaResults?.values

  const { storeInitialized, updateFormulaResults } = useGridStoreInitializer({
    data: data,
    compact: false,
    updatedAt: dataUpdatedAt,
  })

  useEffect(() => {
    if (formulaValues) {
      updateFormulaResults(formulaValues)
    }
  }, [formulaValues])

  return (
    <div style={{ position: "relative", minHeight: 200, minWidth: 200 }}>
      <div
        style={{
          minWidth: "max-content",
        }}
      >
        {data && storeInitialized ? (
          <SuspenseWithErrorBoundary>
            <DepartmentDutyRosterSpreadsheet
              departmentTreeNodes={departmentTreeNodes}
              focus="cast-planning"
              onRefetch={() => {
                return refetch()
              }}
              onQueueLoadingChange={setIsQuickQueueLoading}
              onQueueStatsChange={setQuickQueueStats}
              onQueueCancelRegister={(cancel) => {
                setQuickQueueCancel(() => cancel)
              }}
              data={data}
            />
          </SuspenseWithErrorBoundary>
        ) : null}
      </div>
      <QuickQueueWidget stats={quickQueueStats} onCancel={quickQueueCancel} />
      <LoadingOverlay
        visible={isFetching || isQuickQueueLoading}
        zIndex={555}
        overlayProps={{ radius: "sm", blur: 0 }}
      />
    </div>
  )
}
